import { Input, Modal as AntModal, Select } from 'antd'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { MdAdd, MdCheck, MdExpandMore } from 'react-icons/md'

import { formatAppDate } from '@/Ship/Helpers/appTime'
import { cx } from '@/Ship/Helpers/cssClass'
import { offlineSyncManager } from '@/Ship/Pwa/OfflineSync'
import { pwaManager } from '@/Ship/Pwa/PwaManager'

import { CHECKLIST, type DriverShift, type MyShiftStore, formatShiftDay } from '../../Stores/MyShiftStore'

import { CloseShiftSheet } from './CloseShiftSheet'

import styles from './MyShiftCard.module.css'

/** Час початку в поясі застосунку (Київ), а не пристрою */
const formatTime = (value: string | null | undefined): string => formatAppDate(value, 'DD.MM, HH:mm') ?? '—'

/** Рядок незакритої зміни: дата й ТЗ, «Відкрити зміну» або причина, чому зараз не можна */
const PendingShift = observer(({ store, shift }: { store: MyShiftStore; shift: DriverShift }) => {
    const reason = store.blockReason(shift)
    const meta = [shift.vehicle?.title, shift.cluster?.title].filter(Boolean).join(' · ')

    return (
        <div className={cx(styles, 'pending')} data-testid="pending-shift">
            <div className={cx(styles, 'pendingMain')}>
                <span className={cx(styles, 'pendingDate')}>
                    {formatShiftDay(shift.shiftDate)}
                    <span>{shift.code}</span>
                </span>
                {meta && <span className={cx(styles, 'pendingMeta')}>{meta}</span>}
            </div>
            {reason ? (
                <span className={cx(styles, 'pendingReason')}>{reason}</span>
            ) : (
                <button
                    type="button"
                    className="pwa-btn pwa-btn--primary pwa-btn--compact"
                    onClick={() => store.openStart(shift)}
                >
                    Відкрити зміну
                </button>
            )}
        </div>
    )
})

/** Пункт чек-листа готовності: велика кнопка-перемикач */
const CheckItem = ({
    checked,
    onChange,
    children,
}: {
    checked: boolean
    onChange: (v: boolean) => void
    children: string
}) => (
    <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        className={`${cx(styles, 'check')}${checked ? ` ${cx(styles, 'check--on')}` : ''}`}
        onClick={() => onChange(!checked)}
    >
        <span className={cx(styles, 'checkBox')} aria-hidden="true">
            {checked && <MdCheck />}
        </span>
        <span>{children}</span>
    </button>
)

interface MyShiftCardProps {
    store: MyShiftStore
    /** Скільки завдань активної зміни зараз у роботі: зміну не завершити, поки їх не завершено */
    inProgressCount: number
}

/**
 * Блок зміни над списком завдань. З активною зміною — один рядок, що розкривається
 * в деталі й кнопку «Закрити зміну»; без неї — незакриті зміни з «Відкрити зміну», а коли
 * відкрити нічого — «Створити зміну» (той самий поп-ап плюс дата й регіон, кластер — з регіону).
 * Поп-ап відкриття: ТЗ, початковий одометр (з підказкою мінімуму) і чек-лист.
 * Закриття — шторка CloseShiftSheet (попередження → дані → перевірка).
 */
export const MyShiftCard = observer(({ store, inProgressCount }: MyShiftCardProps) => {
    const active = store.active
    const pending = store.pending
    const startShift = store.startShift
    const [open, setOpen] = useState(false)
    // Офлайн-дії ще не дійшли до сервера: він не бачить завдань у роботі й дозволив би
    // завершити зміну, а потім синхронізована дія лишила б завдання без зміни
    const syncPending = offlineSyncManager.pendingCount > 0 || offlineSyncManager.isSyncing

    return (
        <>
            <section className="pwa-card" data-testid="my-shift">
                {active ? (
                    <>
                        <button
                            type="button"
                            className={cx(styles, 'strip')}
                            onClick={() => setOpen((v) => !v)}
                            aria-expanded={open}
                        >
                            <span className={cx(styles, 'live')} aria-hidden="true" />
                            <span className={cx(styles, 'stripMain')}>
                                <span className={cx(styles, 'stripTitle')}>{active.vehicle?.title ?? 'На зміні'}</span>
                                <span className={cx(styles, 'stripMeta')}>
                                    Зміна {formatShiftDay(active.shiftDate)} · з {formatTime(active.startedAt)}
                                    {active.cluster?.title ? ` · ${active.cluster.title}` : ''}
                                </span>
                            </span>
                            <MdExpandMore
                                className={`${cx(styles, 'chevron')}${open ? ` ${cx(styles, 'chevron--open')}` : ''}`}
                            />
                        </button>

                        {open && (
                            <div className={cx(styles, 'expanded')}>
                                <div className={cx(styles, 'grid')}>
                                    <div className={cx(styles, 'cell')}>
                                        <span className="pwa-label">Код зміни</span>
                                        <span className="pwa-value">{active.code}</span>
                                    </div>
                                    <div className={cx(styles, 'cell')}>
                                        <span className="pwa-label">Регіон</span>
                                        <span className="pwa-value">{active.region?.title ?? '—'}</span>
                                    </div>
                                    <div className={cx(styles, 'cell')}>
                                        <span className="pwa-label">Початок</span>
                                        <span className="pwa-value">{formatTime(active.startedAt)}</span>
                                    </div>
                                    <div className={cx(styles, 'cell')}>
                                        <span className="pwa-label">Початковий одометр</span>
                                        <span className="pwa-value">
                                            {typeof active.startOdometer === 'number'
                                                ? `${active.startOdometer} км`
                                                : '—'}
                                        </span>
                                    </div>
                                </div>
                                {store.fromCache && (
                                    <span className={cx(styles, 'note')}>Немає з'єднання — дані зміни з кешу</span>
                                )}
                                <button
                                    type="button"
                                    className="pwa-btn pwa-btn--secondary pwa-btn--block"
                                    onClick={() => store.openComplete()}
                                >
                                    Закрити зміну
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <div className={cx(styles, 'head')}>
                            <span className={cx(styles, 'headTitle')}>Зміна</span>
                            {store.loading && !store.loaded && <span className="pwa-muted">Завантаження…</span>}
                        </div>
                        {!store.loading && store.loadError && (
                            <div className={cx(styles, 'muted')}>{store.loadError}</div>
                        )}
                        {store.fromCache && (
                            <div className={cx(styles, 'cache')}>Немає з'єднання — дані змін з кешу</div>
                        )}
                        {store.loaded && pending.length === 0 && !store.canCreate && (
                            <div className={cx(styles, 'muted')}>Незакритих змін немає</div>
                        )}
                        {pending.map((shift) => (
                            <PendingShift key={shift.uuid} store={store} shift={shift} />
                        ))}
                        {/* Відкрити нічого (немає нової зміни на сьогодні чи раніше) — водій створює її сам */}
                        {store.canCreate && (
                            <div className={cx(styles, 'create')} data-testid="create-shift">
                                <span className={cx(styles, 'createText')}>
                                    {pending.length > 0
                                        ? 'На сьогодні зміни немає — можна створити нову'
                                        : 'Змін на сьогодні немає — створіть нову, щоб почати роботу'}
                                </span>
                                <button
                                    type="button"
                                    className="pwa-btn pwa-btn--primary pwa-btn--block"
                                    disabled={pwaManager.isOffline}
                                    onClick={() => store.openCreate()}
                                >
                                    <MdAdd /> Створити зміну
                                </button>
                                {pwaManager.isOffline && (
                                    <span className={cx(styles, 'note')}>
                                        Немає з'єднання — створити зміну можна, коли з'явиться мережа
                                    </span>
                                )}
                            </div>
                        )}
                    </>
                )}
            </section>

            <AntModal
                open={store.startOpen}
                title={
                    store.creating
                        ? 'Нова зміна'
                        : startShift
                          ? `Відкриття зміни · ${formatShiftDay(startShift.shiftDate)}`
                          : 'Відкриття зміни'
                }
                onCancel={() => store.close()}
                onOk={() => store.start(pwaManager.isOffline)}
                okText={store.creating ? 'Створити й розпочати' : 'Відкрити зміну'}
                cancelText="Скасувати"
                okButtonProps={{ disabled: !store.startValid, loading: store.submitting, size: 'large' }}
                cancelButtonProps={{ size: 'large' }}
                destroyOnHidden
            >
                <div className={cx(styles, 'form')}>
                    {store.creating && (
                        <>
                            <div className={cx(styles, 'field')}>
                                <span className={cx(styles, 'fieldLabel')}>Дата зміни</span>
                                <span className={cx(styles, 'fixedValue')} data-testid="shift-create-date">
                                    {store.createDate ? formatShiftDay(store.createDate) : '—'}
                                </span>
                            </div>
                            <div className={cx(styles, 'field')}>
                                <span className={cx(styles, 'fieldLabel')}>Регіон</span>
                                <Select
                                    showSearch
                                    size="large"
                                    optionFilterProp="label"
                                    placeholder="Оберіть регіон"
                                    loading={store.vehiclesLoading}
                                    notFoundContent={
                                        store.vehiclesLoading ? 'Завантаження…' : 'Немає доступних регіонів'
                                    }
                                    value={store.regionCode}
                                    onChange={(value: string) => store.setRegion(value)}
                                    options={store.regions.map((r) => ({ value: r.code, label: r.title ?? r.code }))}
                                    data-testid="shift-region"
                                />
                                {store.selectedRegion && (
                                    <span className={cx(styles, 'note')}>
                                        Кластер: {store.selectedRegion.cluster.title ?? '—'}
                                    </span>
                                )}
                            </div>
                        </>
                    )}
                    <div className={cx(styles, 'field')}>
                        <span className={cx(styles, 'fieldLabel')}>Транспортний засіб</span>
                        <Select
                            showSearch
                            size="large"
                            optionFilterProp="label"
                            placeholder="Оберіть ТЗ"
                            loading={store.vehiclesLoading}
                            notFoundContent={store.vehiclesLoading ? 'Завантаження…' : 'Немає доступних ТЗ'}
                            value={store.vehicleUuid}
                            onChange={(value: string) => store.setVehicle(value)}
                            options={store.vehicles.map((v) => ({ value: v.uuid, label: v.title ?? v.uuid }))}
                            data-testid="shift-vehicle"
                        />
                    </div>
                    <div className={cx(styles, 'field')}>
                        <span className={cx(styles, 'fieldLabel')}>Початковий одометр, км</span>
                        <Input
                            size="large"
                            inputMode="numeric"
                            placeholder="Напр. 125400"
                            value={store.startOdometer}
                            {...(store.startOdometerError ? { status: 'error' as const } : {})}
                            onChange={(e) => store.setStartOdometer(e.target.value)}
                            data-testid="shift-start-odometer"
                        />
                        {store.startOdometerError && (
                            <span className={cx(styles, 'hint')}>{store.startOdometerError}</span>
                        )}
                        {!store.startOdometerError && store.minOdometer !== null && (
                            <span className={cx(styles, 'note')}>
                                Підставлено кінцевий одометр попередньої зміни цього ТЗ — менше вказати не можна
                            </span>
                        )}
                    </div>
                    <div className={cx(styles, 'field')}>
                        <span className={cx(styles, 'fieldLabel')}>Чек-лист готовності</span>
                        <div className={cx(styles, 'checklist')}>
                            <CheckItem
                                checked={store.documentsConfirmed}
                                onChange={(v) => store.setDocumentsConfirmed(v)}
                            >
                                {CHECKLIST.documents}
                            </CheckItem>
                            <CheckItem
                                checked={store.safetyBriefingConfirmed}
                                onChange={(v) => store.setSafetyBriefingConfirmed(v)}
                            >
                                {CHECKLIST.safetyBriefing}
                            </CheckItem>
                        </div>
                    </div>
                    {store.formError && <div className="pwa-note pwa-note--danger">{store.formError}</div>}
                </div>
            </AntModal>

            <CloseShiftSheet
                store={store}
                isOffline={pwaManager.isOffline}
                inProgressCount={inProgressCount}
                syncPending={syncPending}
            />
        </>
    )
})
